import { css } from '@/styled-system/css';

// Dimensions that don't map cleanly to Panda spacing tokens. Centralized so
// related values stay in lock-step (e.g., the listing thumbnail and its
// no-photo placeholder must always be the same size).
const THUMBNAIL_SIZE = '100px';
const THUMBNAIL_RADIUS = '4px';
const THUMBNAIL_HOVER_SCALE = 1.03;
const THUMBNAIL_HOVER_TRANSITION = '0.2s';
const ARROW_BUTTON_SIZE = '36px';
const ARROW_ICON_SIZE = '20px';
const ARROW_BUTTON_SHADOW = '0 2px 8px rgba(0, 0, 0, 0.15)';
const CHECKMARK_ICON_SIZE = '16px';
const FOCUS_OUTLINE_WIDTH = '2px';
const FOCUS_OUTLINE_OFFSET = '2px';
const HAIRLINE_BORDER_WIDTH = '1px';
const LISTING_TITLE_LINE_HEIGHT = 1.3;

export const container = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '2x',
  minWidth: 0,
  maxWidth: '100%',
});

export const tableWrapper = css({
  position: 'relative',
  width: '100%',
  minWidth: 0,
  maxWidth: '100%',
});

export const scrollContainer = css({
  width: '100%',
  minWidth: 0,
  maxWidth: '100%',
  overflowX: 'auto',
  overflowY: 'hidden',
  WebkitOverflowScrolling: 'touch',
  scrollBehavior: 'smooth',
  paddingRight: '6x',
  paddingBottom: '1x',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  '@media (prefers-reduced-motion: reduce)': {
    scrollBehavior: 'auto',
  },
});

export const table = css({
  tableLayout: 'fixed',
  borderCollapse: 'collapse',
});

export const rowLabelHeader = css({
  padding: 0,
  position: 'sticky',
  left: 0,
  zIndex: 1,
  backgroundColor: 'hpxWhite',
});

export const listingHeader = css({
  paddingTop: 0,
  paddingRight: '1x',
  paddingBottom: 0,
  paddingLeft: '1x',
  textAlign: 'left',
  verticalAlign: 'top',
  fontWeight: 'normal',
});

export const currentListingHeader = css({
  paddingLeft: '2x',
});

export const columnHeader = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '1x',
  paddingBottom: '2x',
  textDecoration: 'none',
  color: 'inherit',
  cursor: 'pointer',
  _hover: {
    '& img': {
      transform: `scale(${THUMBNAIL_HOVER_SCALE})`,
    },
  },
});

export const columnHeaderCurrent = css({
  cursor: 'default',
  _hover: {
    '& img': {
      transform: 'none',
    },
  },
});

export const listingImageWrapper = css({
  width: THUMBNAIL_SIZE,
  height: THUMBNAIL_SIZE,
  borderRadius: THUMBNAIL_RADIUS,
  overflow: 'hidden',
  flexShrink: 0,
});

export const listingImage = css({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  backgroundColor: 'hpxGrey100',
  transitionProperty: 'transform',
  transitionDuration: THUMBNAIL_HOVER_TRANSITION,
});

export const noPhotoPlaceholder = css({
  width: THUMBNAIL_SIZE,
  height: THUMBNAIL_SIZE,
  borderRadius: THUMBNAIL_RADIUS,
  backgroundColor: 'hpxGrey100',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'hpxGrey500',
  fontSize: 'tiny',
});

export const listingName = css({
  fontSize: 'sm',
  fontWeight: '700',
  color: 'hpxTeal600',
  lineHeight: LISTING_TITLE_LINE_HEIGHT,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  overflowWrap: 'break-word',
  margin: 0,
});

export const currentListingText = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '0',
});

export const currentListingLabel = css({
  fontSize: 'sm',
  fontWeight: '700',
  color: 'hpxBlue600',
  lineHeight: LISTING_TITLE_LINE_HEIGHT,
  margin: 0,
});

export const currentListingName = css({
  fontSize: 'sm',
  fontWeight: '400',
  color: 'hpxBlue500',
  lineHeight: LISTING_TITLE_LINE_HEIGHT,
  overflowWrap: 'break-word',
  margin: 0,
});

export const rowLabelCell = css({
  fontSize: 'sm',
  fontWeight: '400',
  color: 'hpxGrey600',
  textAlign: 'right',
  paddingTop: '1x',
  paddingRight: '1x',
  paddingBottom: '1x',
  paddingLeft: 0,
  borderRightWidth: HAIRLINE_BORDER_WIDTH,
  borderRightStyle: 'solid',
  borderRightColor: 'hpxGrey400',
  whiteSpace: 'nowrap',
  verticalAlign: 'top',
  position: 'sticky',
  left: 0,
  zIndex: 1,
  backgroundColor: 'hpxWhite',
});

export const rowLabelCellStriped = css({
  backgroundColor: 'hpxGrey100',
});

export const valueCell = css({
  fontSize: 'sm',
  color: 'hpxBlue600',
  paddingTop: '1x',
  paddingRight: '1x',
  paddingBottom: '1x',
  paddingLeft: '2x',
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
  verticalAlign: 'top',
});

export const stripedRow = css({
  backgroundColor: 'hpxGrey100',
});

export const priceCell = css({
  fontWeight: '700',
});

export const totalPriceBadgeCell = css({
  whiteSpace: 'normal',
  overflow: 'visible',
  paddingTop: 0,
  paddingBottom: '1x',
});

export const firstBodyRow = css({
  paddingTop: '2x',
});

export const checkmarkIcon = css({
  width: CHECKMARK_ICON_SIZE,
  height: CHECKMARK_ICON_SIZE,
});

export { visuallyHidden as srOnly } from 'app/shared/halo/styles/visuallyHidden';

export const arrowButton = css({
  position: 'absolute',
  top: '5x',
  zIndex: 'zIndexStepOver',
  width: ARROW_BUTTON_SIZE,
  height: ARROW_BUTTON_SIZE,
  borderRadius: '50%',
  border: 'none',
  backgroundColor: 'hpxWhite',
  boxShadow: ARROW_BUTTON_SHADOW,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'hpxBlue600',
  '& > svg': {
    width: ARROW_ICON_SIZE,
    height: ARROW_ICON_SIZE,
  },
  _hover: {
    backgroundColor: 'hpxGrey100',
  },
  _focus: {
    outlineWidth: FOCUS_OUTLINE_WIDTH,
    outlineStyle: 'solid',
    outlineColor: 'hpxTeal500',
    outlineOffset: FOCUS_OUTLINE_OFFSET,
  },
});

export const arrowButtonLeft = css({
  left: 'xs',
});

export const arrowButtonRight = css({
  right: 'xs',
});

export const areaLinkContainer = css({
  display: 'flex',
  justifyContent: 'flex-end',
  paddingTop: '1x',
});

export const areaLink = css({
  fontSize: 'sm',
  fontWeight: '600',
  color: 'hpxTeal500',
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: 'xs',
  _hover: {
    color: 'hpxTeal600',
    textDecoration: 'underline',
  },
});
